import { memo } from 'react';
import { TimePickerSelect } from './TimePickerSelect';

export interface DayOperatingHours {
  dayOfWeek: number; // 0=일요일, 1=월요일, ..., 6=토요일
  isClosed: boolean;
  openTime: string;
  closeTime: string;
  breakStartTime: string;
  breakEndTime: string;
  hasBreakTime: boolean;
}

interface WeeklyOperatingHoursProps {
  operatingHours: DayOperatingHours[];
  onChange: (operatingHours: DayOperatingHours[]) => void;
}

const DAY_NAMES = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];

export const WeeklyOperatingHours = memo(function WeeklyOperatingHours({
  operatingHours,
  onChange
}: WeeklyOperatingHoursProps) {
  const handleDayChange = (dayIndex: number, field: keyof DayOperatingHours, value: string | boolean) => {
    const newHours = [...operatingHours];
    const day = { ...newHours[dayIndex] };
    
    if (field === 'isClosed' && value === true) {
      // 영업안함 체크 시 모든 시간 정보 초기화
      day.isClosed = true;
      day.openTime = '';
      day.closeTime = '';
      day.breakStartTime = '';
      day.breakEndTime = '';
      day.hasBreakTime = false;
    } else if (field === 'isClosed' && value === false) {
      // 영업함 체크 시
      day.isClosed = false;
    } else if (field === 'hasBreakTime' && value === false) {
      // 브레이크타임 없음 체크 시 브레이크타임 초기화
      day.hasBreakTime = false;
      day.breakStartTime = '';
      day.breakEndTime = '';
    } else if (field === 'hasBreakTime' && value === true) {
      // 브레이크타임 있음 체크 시
      day.hasBreakTime = true;
    } else {
      // 다른 필드 변경
      (day as any)[field] = value;
    }
    
    newHours[dayIndex] = day;
    onChange(newHours);
  };

  const handleApplyToAll = (dayIndex: number) => {
    const sourceDay = operatingHours[dayIndex];
    const newHours = operatingHours.map((day, index) => {
      if (index === dayIndex) return day; // 원본 요일은 그대로
      
      return {
        ...day,
        isClosed: sourceDay.isClosed,
        openTime: sourceDay.openTime,
        closeTime: sourceDay.closeTime,
        breakStartTime: sourceDay.breakStartTime,
        breakEndTime: sourceDay.breakEndTime,
        hasBreakTime: sourceDay.hasBreakTime
      };
    });
    
    onChange(newHours);
  };

  return (
    <div className="space-y-4 relative">
      <label className="block text-base font-medium text-[#222222] mb-2">
        운영 시간
      </label>
      
      <div className="space-y-3 overflow-visible">
        {operatingHours.map((day, index) => (
          <div key={day.dayOfWeek} className="border border-[#DEDEDE] rounded-lg p-3 overflow-visible">
            {/* 요일 헤더 */}
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-[#222222]">{DAY_NAMES[day.dayOfWeek]}</h4>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1 text-sm">
                  <input
                    type="checkbox"
                    checked={day.isClosed}
                    onChange={(e) => handleDayChange(index, 'isClosed', e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-[#666666]">영업안함</span>
                </label>
                {!day.isClosed && (day.openTime || day.closeTime) && (
                  <button
                    type="button"
                    onClick={() => handleApplyToAll(index)}
                    className="px-2 py-1 text-xs bg-[#51C854] text-white rounded hover:bg-[#46B649] transition-colors"
                  >
                    모두 적용
                  </button>
                )}
              </div>
            </div>

            {/* 운영시간 입력 */}
            {!day.isClosed && (
              <div className="space-y-3">
                {/* 영업시간 */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm text-[#666666] w-16">영업시간</span>
                    <div className="flex items-center gap-2 flex-1">
                      <TimePickerSelect
                        value={day.openTime}
                        onChange={(value) => handleDayChange(index, 'openTime', value)}
                        placeholder="오픈 시간"
                        className="flex-1"
                      />
                      <span className="text-[#666666]">~</span>
                      <TimePickerSelect
                        value={day.closeTime}
                        onChange={(value) => handleDayChange(index, 'closeTime', value)}
                        placeholder="마감 시간"
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>

                {/* 브레이크타임 설정 */}
                <div className="relative">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm text-[#666666] w-16">브레이크</span>
                    <label className="flex items-center gap-1 text-sm">
                      <input
                        type="checkbox"
                        checked={!day.hasBreakTime}
                        onChange={(e) => handleDayChange(index, 'hasBreakTime', !e.target.checked)}
                        className="w-4 h-4"
                      />
                      <span className="text-[#666666]">브레이크타임 없음</span>
                    </label>
                  </div>
                  
                  {/* 브레이크타임 입력 */}
                  {day.hasBreakTime && (
                    <div className="flex items-center gap-2 relative z-10">
                      <span className="text-sm text-[#666666] w-16"></span>
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div className="flex-1 min-w-0">
                          <TimePickerSelect
                            value={day.breakStartTime}
                            onChange={(value) => handleDayChange(index, 'breakStartTime', value)}
                            placeholder="시작시간"
                            className="w-full"
                          />
                        </div>
                        <span className="text-[#666666] flex-shrink-0">~</span>
                        <div className="flex-1 min-w-0">
                          <TimePickerSelect
                            value={day.breakEndTime}
                            onChange={(value) => handleDayChange(index, 'breakEndTime', value)}
                            placeholder="종료시간"
                            className="w-full"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
});

// 기본 운영시간 데이터 생성 함수
export const createDefaultOperatingHours = (): DayOperatingHours[] => {
  return Array.from({ length: 7 }, (_, index) => ({
    dayOfWeek: index,
    isClosed: false,
    openTime: '',
    closeTime: '',
    breakStartTime: '',
    breakEndTime: '',
    hasBreakTime: false
  }));
};